import {
  confirmResetPassword as v6ConfirmResetPassword,
  confirmSignIn as v6ConfirmSignIn,
  confirmSignUp as v6ConfirmSignUp,
  fetchAuthSession as v6FetchAuthSession,
  fetchMFAPreference as v6FetchMFAPreference,
  fetchUserAttributes as v6FetchUserAttributes,
  getCurrentUser as v6GetCurrentUser,
  resetPassword as v6ResetPassword,
  setUpTOTP as v6SetUpTOTP,
  signIn as v6SignIn,
  signInWithRedirect as v6SignInWithRedirect,
  signOut as v6SignOut,
  signUp as v6SignUp,
  updateMFAPreference as v6UpdateMFAPreference,
  updatePassword as v6UpdatePassword,
  verifyTOTPSetup as v6VerifyTOTPSetup,
} from "aws-amplify/auth";

// Compatibility wrapper that exposes the AWS Amplify v4 `Auth` surface on
// top of v6's modular auth functions.
//
// In v4 a single `Auth` static class held everything; in v6 each method is
// a free function under `aws-amplify/auth`, and a few API shapes changed:
//
//   - `Auth.signIn(u, p)` returned a `CognitoUser` with `.attributes` etc.
//     v6 returns `{ isSignedIn, nextStep }`. The wrapper hydrates a v4-shaped
//     user object via `getCurrentUser` + `fetchUserAttributes` after a clean
//     sign-in, and throws an error carrying `.nextStep` when an MFA / new-
//     password challenge is required (matching the v4 error path the
//     existing Login flow already handles).
//   - `Auth.currentSession()` returned a `CognitoUserSession`. v6's
//     `fetchAuthSession()` returns tokens directly. The wrapper exposes the
//     v4 `getIdToken().getJwtToken()` / `getRefreshToken()` / `isValid()`
//     methods backed by the v6 token objects.
//   - TOTP setup / verification / preference no longer take a user object
//     (they read the currently authenticated user implicitly).
//
// `CognitoHostedUIIdentityProvider` is a v4-only enum; v6 expects provider
// names as plain strings. The wrapper re-exports the enum so existing
// imports keep working.

export const CognitoHostedUIIdentityProvider = {
  Cognito: "COGNITO",
  Google: "Google",
  Facebook: "Facebook",
  Amazon: "Amazon",
  Apple: "Apple",
};

const hydrateV4User = async () => {
  const [user, attributes] = await Promise.all([
    v6GetCurrentUser(),
    v6FetchUserAttributes().catch(() => ({})),
  ]);
  return {
    username: user.username,
    userId: user.userId,
    attributes,
    signInDetails: user.signInDetails,
  };
};

const tokensToV4Session = (session) => {
  const idToken = session?.tokens?.idToken;
  const accessToken = session?.tokens?.accessToken;
  return {
    getIdToken: () => ({
      getJwtToken: () => idToken?.toString() ?? "",
      payload: idToken?.payload ?? {},
    }),
    getAccessToken: () => ({
      getJwtToken: () => accessToken?.toString() ?? "",
      payload: accessToken?.payload ?? {},
    }),
    getRefreshToken: () => ({
      getToken: () => null,
    }),
    isValid: () => {
      const exp = idToken?.payload?.exp;
      return typeof exp === "number" ? exp * 1000 > Date.now() : false;
    },
  };
};

// v6 throws `UserAlreadyAuthenticatedException` (or similar) when a session
// already exists in storage and you call signIn again. v4 silently re-signed
// the user in. To preserve v4 semantics, intercept that error, sign the
// existing session out, and retry once.
const signInWithRetry = async (username, password) => {
  try {
    return await v6SignIn({ username, password });
  } catch (err) {
    const msg = String(err?.name || err?.message || "");
    if (
      msg.includes("UserAlreadyAuthenticated") ||
      msg.includes("already a signed in user")
    ) {
      try {
        await v6SignOut();
      } catch {
        /* ignore */
      }
      return await v6SignIn({ username, password });
    }
    throw err;
  }
};

export const Auth = {
  signIn: async (usernameOrParams, maybePassword) => {
    const username =
      typeof usernameOrParams === "string"
        ? usernameOrParams
        : usernameOrParams?.username;
    const password =
      typeof usernameOrParams === "string"
        ? maybePassword
        : usernameOrParams?.password;

    const result = await signInWithRetry(username, password);
    if (!result.isSignedIn) {
      const err = new Error(
        `Sign-in requires next step: ${result.nextStep?.signInStep}`,
      );
      err.name = "SignInNextStep";
      err.code = result.nextStep?.signInStep;
      err.nextStep = result.nextStep;
      throw err;
    }
    return await hydrateV4User();
  },

  signOut: async () => {
    return v6SignOut();
  },

  currentAuthenticatedUser: async () => {
    return hydrateV4User();
  },

  currentSession: async () => {
    const session = await v6FetchAuthSession();
    if (!session?.tokens?.idToken) {
      const err = new Error("No current user");
      err.name = "NoUserSession";
      throw err;
    }
    return tokensToV4Session(session);
  },

  // v4 `Auth.currentCredentials()` hydrated Cognito Identity Pool AWS
  // credentials used for IAM-signed API Gateway calls. Login.js and App.js
  // await this before the first API.get so SigV4 has keys ready.
  currentCredentials: async () => {
    const session = await v6FetchAuthSession();
    if (!session?.credentials) {
      const err = new Error("No credentials");
      err.name = "NoCredentials";
      throw err;
    }
    return {
      accessKeyId: session.credentials.accessKeyId,
      secretAccessKey: session.credentials.secretAccessKey,
      sessionToken: session.credentials.sessionToken,
      expiration: session.credentials.expiration,
      identityId: session.identityId,
      authenticated: !!session.tokens?.idToken,
    };
  },

  // v4 callers do `Auth.refreshSession(refreshToken)`; in v6 session refresh
  // is handled internally by fetchAuthSession({ forceRefresh: true }).
  refreshSession: async () => {
    const session = await v6FetchAuthSession({ forceRefresh: true });
    return tokensToV4Session(session);
  },

  signUp: async (params) => {
    const {
      username,
      password,
      attributes = {},
      autoSignIn,
    } = params || {};
    const result = await v6SignUp({
      username,
      password,
      options: {
        userAttributes: attributes,
        autoSignIn: autoSignIn === true,
      },
    });
    return {
      userSub: result.userId,
      userConfirmed: result.isSignUpComplete,
      user: { username },
    };
  },

  confirmSignUp: async (username, confirmationCode) => {
    const result = await v6ConfirmSignUp({ username, confirmationCode });
    return result.isSignUpComplete ? "SUCCESS" : result;
  },

  setupTOTP: async () => {
    const result = await v6SetUpTOTP();
    return result.sharedSecret;
  },

  verifyTotpToken: async (_user, code) => {
    return v6VerifyTOTPSetup({ code });
  },

  setPreferredMFA: async (_user, mfaType) => {
    const totp = mfaType === "TOTP" ? "PREFERRED" : "DISABLED";
    return v6UpdateMFAPreference({ totp });
  },

  getPreferredMFA: async () => {
    const pref = await v6FetchMFAPreference();
    if (pref?.preferred === "TOTP") return "SOFTWARE_TOKEN_MFA";
    return "NOMFA";
  },

  changePassword: async (_user, oldPassword, newPassword) => {
    return v6UpdatePassword({ oldPassword, newPassword });
  },

  forgotPassword: async (username) => {
    return v6ResetPassword({ username });
  },

  forgotPasswordSubmit: async (username, code, newPassword) => {
    return v6ConfirmResetPassword({
      username,
      confirmationCode: code,
      newPassword,
    });
  },

  federatedSignIn: async (params) => {
    const provider = params?.provider ?? params;
    return v6SignInWithRedirect({ provider });
  },

  confirmSignIn: async (_user, challengeResponse) => {
    return v6ConfirmSignIn({ challengeResponse });
  },
};

export default Auth;
